<!-- src/views/pages/finance-accounts/sales/edit-invoices.vue -->
<script setup>
import { ref, reactive, computed, onMounted, nextTick, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useInvoiceStore } from "@/stores/invoiceStore";
import { useCompanyStore } from "@/stores/companyStore";
import { useCrmClientStore } from "@/stores/crmClientStore";
import { useProjectStore } from "@/stores/projectStore";
import { useTaxStore } from "@/stores/taxStore";
import * as Toast from "@/utils/toast";
import InvoicesModal from "@/components/modal/invoices-modal.vue";

/* ---------------- Toast helpers (keep compatible) ---------------- */
const notifyError = (msg) => {
  if (Toast?.toast?.error) return Toast.toast.error(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "error");
  if (Toast?.error) return Toast.error(msg);
  if (typeof Toast === "function") return Toast(msg, "error");
  console.error(msg);
};

const notifySuccess = (msg) => {
  if (Toast?.toast?.success) return Toast.toast.success(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "success");
  if (Toast?.success) return Toast.success(msg);
  if (typeof Toast === "function") return Toast(msg, "success");
  console.log(msg);
};

const router = useRouter();
const route = useRoute();
const invoiceStore = useInvoiceStore();
const companyStore = useCompanyStore();
const clientStore = useCrmClientStore();
const projectStore = useProjectStore();
const taxStore = useTaxStore();

const { activeCompanyId, companies } = storeToRefs(companyStore);
const { items: projectItems } = storeToRefs(projectStore);
const { items: taxItems } = storeToRefs(taxStore);

/* ---------------- Dates ---------------- */
const currentDate = ref(new Date());
const currentDateOne = ref(new Date());
const startdate = currentDate;
const startdateOne = currentDateOne;
const dateFormat = ref("dd-MM-yyyy");

const Paymenttype = ref([
  "Cash",
  "Credit Card",
  "Debit Card",
  "Bank Transfer",
  "Mobile Money",
]);

/* ---------------- FORM ---------------- */
const form = reactive({
  id: null,
  title: "",
  invoice_no: "",
  invoice_date: null,
  due_date: null,
  client_id: null,
  client_name: "",
  project_id: null,
  email: "",
  client_address: "",
  billing_address: "",
  reference_no: "",
  payment_type: null,
  bank_name: null,
  description: "",
  notes: "",
  status: "draft",

  tax_id: null,
  tax_name: "",
  tax_rate: null,
  apply_tax: false,

  items: [],
});

const saving = ref(false);
const loadingInvoice = ref(false);
const attempted = ref(false);

/* ---------------- COMPANY ---------------- */
const activeCompany = computed(() => {
  return companies.value?.find((c) => c.id === activeCompanyId.value) || null;
});
const companyLabel = computed(() => activeCompany.value?.company_name || "");

/* ---------------- Helpers ---------------- */
function debounce(fn, ms = 300) {
  let t;
  return (...a) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...a), ms);
  };
}

function formatMoney(value) {
  const num = Number(value || 0);
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function toYmd(date) {
  if (!date) return null;
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/* ---------------- Items / totals ---------------- */
function computeRowTotal(item) {
  const qty = Number(item.qty) || 0;
  const rate = Number(item.rate) || 0;
  const discount = Number(item.discount) || 0;
  const base = qty * rate;
  const total = Math.max(base - discount, 0);
  item.total = total;
  return total;
}

const subTotal = computed(() =>
  form.items.reduce((sum, item) => {
    const qty = Number(item.qty) || 0;
    const rate = Number(item.rate) || 0;
    return sum + qty * rate;
  }, 0)
);

const discountTotal = computed(() =>
  form.items.reduce((sum, item) => sum + (Number(item.discount) || 0), 0)
);

const selectedTax = computed(() => {
  if (!form.tax_id) return null;
  return taxItems.value.find((t) => t.id === form.tax_id) || null;
});

const taxRate = computed(() => {
  if (!form.apply_tax) return 0;
  if (form.tax_id && selectedTax.value)
    return Number(selectedTax.value.rate || 0);
  if (form.tax_rate != null) return Number(form.tax_rate);
  return 0;
});

const taxTotal = computed(() => {
  const taxable = subTotal.value - discountTotal.value;
  if (!taxRate.value) return 0;
  return (taxable * taxRate.value) / 100;
});

const grandTotal = computed(
  () => subTotal.value - discountTotal.value + taxTotal.value
);

const totalsForPreview = computed(() => ({
  subTotal: subTotal.value,
  discountTotal: discountTotal.value,
  taxTotal: taxTotal.value,
  grandTotal: grandTotal.value,
}));

function addItem() {
  form.items.push({
    description: "",
    qty: 1,
    rate: 0,
    discount: 0,
    total: 0,
  });
  nextTick(() => {
    const last = document.querySelector(".dq-item-desc-last");
    last?.focus?.();
  });
}

function removeItem(index) {
  if (form.items.length === 1) return;
  form.items.splice(index, 1);
}

/* ---------------- Reference helper ---------------- */
function generateReference() {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const min = 6;
  const max = 8;
  const len = Math.floor(Math.random() * (max - min + 1)) + min;
  let out = "";
  for (let i = 0; i < len; i++)
    out += chars.charAt(Math.floor(Math.random() * chars.length));
  return out;
}

/* =========================================================
   PREMIUM SEARCH DROPDOWN (Clients + Taxes) with keyboard
========================================================= */
/* CLIENT DROPDOWN */
const clientQuery = ref("");
const clientOptions = ref([]);
const clientsLoading = ref(false);

const clientDdOpen = ref(false);
const clientActiveIndex = ref(0);
const clientInputRef = ref(null);

async function fetchClients(q = "") {
  try {
    clientsLoading.value = true;
    const rows = await clientStore.list({ q, per_page: 10 });
    const list =
      rows?.data?.data || rows?.data || clientStore.clients?.value || [];
    clientOptions.value = (list || []).map((c) => ({
      id: c.id,
      label: c.name || `Client #${c.id}`,
      raw: c,
      meta: c.email || c.phone || "",
    }));
    clientActiveIndex.value = 0;
  } catch (e) {
    notifyError(
      e?.response?.data?.message || e.message || "Failed to load clients"
    );
  } finally {
    clientsLoading.value = false;
  }
}

const debClients = debounce(fetchClients, 250);

function openClientDd() {
  clientDdOpen.value = true;
  nextTick(() => clientInputRef.value?.focus?.());
}
function closeClientDd() {
  clientDdOpen.value = false;
}
function applyClientSnapshot(client, overwrite = true) {
  if (!client) return;

  if (overwrite || !form.email) {
    form.email = client.email || "";
  }
  if (overwrite || !form.client_address) {
    form.client_address = client.address || "";
  }
  if (overwrite || !form.billing_address) {
    form.billing_address = client.address || "";
  }
}
function chooseClient(opt) {
  form.client_id = opt?.id || null;
  form.client_name = opt?.label || "";
  form.project_id = null;
  applyClientSnapshot(opt?.raw, true);
  closeClientDd();
}

function onClientKeydown(e) {
  if (!clientDdOpen.value && (e.key === "ArrowDown" || e.key === "Enter")) {
    e.preventDefault();
    openClientDd();
    return;
  }
  if (!clientDdOpen.value) return;

  const max = clientOptions.value.length - 1;
  if (e.key === "Escape") {
    e.preventDefault();
    closeClientDd();
    return;
  }
  if (e.key === "ArrowDown") {
    e.preventDefault();
    clientActiveIndex.value = Math.min(clientActiveIndex.value + 1, max);
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    clientActiveIndex.value = Math.max(clientActiveIndex.value - 1, 0);
  } else if (e.key === "Enter") {
    e.preventDefault();
    const opt = clientOptions.value[clientActiveIndex.value];
    if (opt) chooseClient(opt);
  }
}

const selectedCustomer = computed(() => {
  const opt = clientOptions.value.find((c) => c.id === form.client_id);
  return opt?.raw || null;
});

const filteredProjectOptions = computed(() => {
  const list = Array.isArray(projectItems.value) ? projectItems.value : [];
  if (!form.client_id) return list;
  return list.filter((project) => Number(project.client_id) === Number(form.client_id));
});

watch(
  () => selectedCustomer.value,
  (client) => {
    if (!client || form.client_id == null) return;
    applyClientSnapshot(client, false);
  },
  { immediate: false }
);

watch(
  filteredProjectOptions,
  (projects) => {
    if (!form.project_id) return;
    const hasMatch = projects.some((project) => Number(project.id) === Number(form.project_id));
    if (!hasMatch) {
      form.project_id = null;
    }
  },
  { deep: true }
);

/* TAX DROPDOWN */
const taxSearch = ref("");
const taxDdOpen = ref(false);
const taxActiveIndex = ref(0);
const taxInputRef = ref(null);

const taxOptions = computed(() =>
  taxItems.value.map((t) => ({
    id: t.id,
    label: `${t.name} (${t.rate}%)`,
    rate: t.rate,
    name: t.name,
    desc: t.description || "",
  }))
);

const filteredTaxOptions = computed(() => {
  const q = taxSearch.value.toLowerCase().trim();
  if (!q) return taxOptions.value;
  return taxOptions.value.filter((t) => t.label.toLowerCase().includes(q));
});

const selectedTaxLabel = computed(() => {
  if (!form.apply_tax) return "Select tax";
  if (!form.tax_id) return "Select tax";
  if (form.tax_name) return form.tax_name;
  const found = taxOptions.value.find((t) => t.id === form.tax_id);
  return found ? found.label : "Select tax";
});

function openTaxDd() {
  taxDdOpen.value = true;
  nextTick(() => taxInputRef.value?.focus?.());
}
function closeTaxDd() {
  taxDdOpen.value = false;
}
function selectTax(opt) {
  form.tax_id = opt.id;
  form.tax_name = opt.label;
  form.tax_rate = opt.rate;
  closeTaxDd();
}
function onTaxKeydown(e) {
  if (!taxDdOpen.value && (e.key === "ArrowDown" || e.key === "Enter")) {
    e.preventDefault();
    openTaxDd();
    return;
  }
  if (!taxDdOpen.value) return;

  const max = filteredTaxOptions.value.length - 1;
  if (e.key === "Escape") {
    e.preventDefault();
    closeTaxDd();
    return;
  }
  if (e.key === "ArrowDown") {
    e.preventDefault();
    taxActiveIndex.value = Math.min(taxActiveIndex.value + 1, max);
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    taxActiveIndex.value = Math.max(taxActiveIndex.value - 1, 0);
  } else if (e.key === "Enter") {
    e.preventDefault();
    const opt = filteredTaxOptions.value[taxActiveIndex.value];
    if (opt) selectTax(opt);
  }
}

/* ---------------- Validation UX ---------------- */
const errors = computed(() => {
  const e = {};
  if (!form.client_id) e.client_id = "Please select a customer.";
  const hasItem = form.items.some((i) => (i.description || "").trim());
  if (!hasItem) e.items = "Add at least one item description.";
  return e;
});

const canSubmit = computed(
  () => !saving.value && Object.keys(errors.value).length === 0
);

/* ---------------- LOAD INVOICE ---------------- */
async function loadInvoice() {
  const id = route.params.id || route.query.id;
  if (!id) {
    router.push("/sales/invoices");
    return;
  }

  loadingInvoice.value = true;
  try {
    const invoice = await invoiceStore.get(id);

    form.id = invoice.id;
    form.title = invoice.title || "";
    form.invoice_no = invoice.invoice_no;
    form.client_id = invoice.client_id;
    form.project_id = invoice.project_id ?? invoice.project?.id ?? null;
    form.email = invoice.email || invoice.client?.email || "";
    form.client_address = invoice.client_address || invoice.client?.address || "";
    form.billing_address = invoice.billing_address || invoice.client_address || invoice.client?.address || "";
    form.reference_no = invoice.reference_no || "";
    form.payment_type = invoice.payment_type || null;
    form.bank_name = invoice.bank_name || null;
    form.description = invoice.description || "";
    form.notes = invoice.notes || "";
    form.status = invoice.status || "draft";

    form.tax_id = invoice.tax_id || null;
    form.tax_name = invoice.tax_name || "";
    form.tax_rate = invoice.tax_rate ?? null;
    form.apply_tax = !!invoice.tax_rate;

    startdate.value = invoice.invoice_date
      ? new Date(invoice.invoice_date)
      : new Date();
    startdateOne.value = invoice.due_date
      ? new Date(invoice.due_date)
      : new Date();

    form.items = (invoice.items || []).map((i) => ({
      description: i.description,
      qty: i.qty,
      rate: i.rate,
      discount: i.discount,
      total: i.total,
    }));

    if (!form.items.length) addItem();
    form.items.forEach((it) => computeRowTotal(it));

    if (invoice.client && invoice.client.name) {
      form.client_name = invoice.client.name;
    } else {
      const opt = clientOptions.value.find((c) => c.id === form.client_id);
      form.client_name = opt?.label || "";
    }

    if (!form.reference_no) {
      form.reference_no = generateReference();
    }
  } catch (e) {
    notifyError("Failed to load invoice");
    router.push("/sales/invoices");
  } finally {
    loadingInvoice.value = false;
  }
}

/* ---------------- SUBMIT ---------------- */
async function submit(status = form.status || "draft") {
  attempted.value = true;

  if (errors.value.client_id) return notifyError(errors.value.client_id);
  if (errors.value.items) return notifyError(errors.value.items);

  saving.value = true;
  form.status = status;

  try {
    form.items.forEach((item) => computeRowTotal(item));

    const payload = {
      title: form.title,
      client_id: form.client_id,
      project_id: form.project_id,
      invoice_date: toYmd(startdate.value),
      due_date: toYmd(startdateOne.value),
      email: form.email || null,
      client_address: form.client_address || null,
      billing_address: form.billing_address || null,
      reference_no: form.reference_no,
      payment_type: form.payment_type,
      bank_name: form.bank_name,
      description: form.description,
      notes: form.notes,
      status: form.status,

      tax_id: form.apply_tax && selectedTax.value ? selectedTax.value.id : null,
      tax_name:
        form.apply_tax && selectedTax.value
          ? selectedTax.value.name
          : form.apply_tax
          ? form.tax_name
          : null,
      tax_rate: form.apply_tax ? taxRate.value : 0,

      items: form.items.map((i) => ({
        description: i.description,
        qty: i.qty,
        rate: i.rate,
        discount: i.discount,
      })),
    };

    await invoiceStore.update(form.id, payload);
    notifySuccess("Invoice updated");
    router.push("/sales/invoices");
  } catch (e) {
    notifyError(e?.response?.data?.message || "Failed to update invoice");
  } finally {
    saving.value = false;
  }
}

function saveDraft() {
  submit("draft");
}
function saveAndSend() {
  submit("pending");
}

/* ---------------- LIFECYCLE ---------------- */
onMounted(async () => {
  if (!companyStore?.companies?.length) {
    await companyStore.fetchCompanies();
  }

  await fetchClients("");
  await projectStore.list({ per_page: 100, sort: "asc" });
  await taxStore.list({ status: "Active", per_page: 50 });

  await loadInvoice();
});
</script>

<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <!-- Loading overlay -->
      <div v-if="loadingInvoice" class="dq-page-overlay">
        <div class="dq-overlay-card">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <div class="dq-overlay-title">Loading invoice…</div>
          <div class="dq-overlay-sub">
            Fetching items, tax snapshot, and customer.
          </div>
        </div>
      </div>

      <!-- Topbar -->
      <div class="dq-topbar">
        <router-link to="/sales/invoices" class="dq-back">
          <span class="dq-back-icon"><i class="ti ti-arrow-left"></i></span>
          Back to Invoices
        </router-link>

        <div class="dq-top-actions">
          <a
            href="javascript:void(0);"
            class="dq-preview"
            data-bs-toggle="modal"
            data-bs-target="#invoice_preview"
          >
            <i class="ti ti-eye me-2"></i>Preview
          </a>
        </div>
      </div>

      <div class="dq-layout" :class="{ 'dq-disabled': loadingInvoice }">
        <!-- LEFT -->
        <div class="dq-main">
          <!-- From -->
          <div class="dq-card dq-from">
            <div class="dq-card-head">
              <div class="dq-card-title">
                <span class="dq-badge-icon"
                  ><i class="ti ti-building"></i
                ></span>
                <div>
                  <div class="dq-h">From</div>
                  <div class="dq-sub">{{ companyLabel }}</div>
                </div>
              </div>

              <span class="dq-chip">
                <i class="ti ti-shield-check me-1"></i> Company profile
              </span>
            </div>

            <div class="dq-card-body">
              <div class="dq-from-name">
                {{ activeCompany?.company_name || "Company Name" }}
              </div>
              <div class="dq-from-line">
                {{ activeCompany?.address || "Company address" }}
              </div>
              <div class="dq-from-line">
                <span class="dq-muted">Email:</span>
                <span class="dq-strong ms-1">{{
                  activeCompany?.email || "company@example.com"
                }}</span>
              </div>
              <div class="dq-from-line">
                <span class="dq-muted">Phone:</span>
                <span class="dq-strong ms-1">{{
                  activeCompany?.phone || "+000 000 000"
                }}</span>
              </div>
            </div>
          </div>

          <!-- Invoice Details -->
          <div class="dq-card">
            <div class="dq-card-head">
              <div class="dq-card-title">
                <span class="dq-badge-icon dq-badge-blue"
                  ><i class="ti ti-file-invoice"></i
                ></span>
                <div>
                  <div class="dq-h">Invoice Details</div>
                  <div class="dq-sub">Update title, dates and reference.</div>
                </div>
              </div>
            </div>

            <div class="dq-card-body">
              <div class="dq-grid-2">
                <div class="dq-field">
                  <label class="dq-label">Invoice Title</label>
                  <input
                    type="text"
                    v-model="form.title"
                    class="form-control dq-input"
                    placeholder="e.g. December retainer, Office supplies"
                  />
                </div>

                <div class="dq-field">
                  <label class="dq-label">Invoice No</label>
                  <input
                    type="text"
                    v-model="form.invoice_no"
                    class="form-control dq-input"
                    disabled
                  />
                </div>
              </div>

              <div class="dq-grid-3">
                <div class="dq-field">
                  <label class="dq-label">Invoice Date</label>
                  <div class="dq-date">
                    <span class="dq-date-ic"
                      ><i class="ti ti-calendar"></i
                    ></span>
                    <date-picker
                      v-model="startdate"
                      class="form-control datetimepicker dq-input"
                      placeholder="dd/mm/yyyy"
                      :editable="true"
                      :clearable="false"
                      :input-format="dateFormat"
                    />
                  </div>
                </div>

                <div class="dq-field">
                  <label class="dq-label">Due Date</label>
                  <div class="dq-date">
                    <span class="dq-date-ic"
                      ><i class="ti ti-calendar"></i
                    ></span>
                    <date-picker
                      v-model="startdateOne"
                      class="form-control datetimepicker dq-input"
                      placeholder="dd/mm/yyyy"
                      :editable="true"
                      :clearable="false"
                      :input-format="dateFormat"
                    />
                  </div>
                </div>

                <div class="dq-field">
                  <label class="dq-label">Reference</label>
                  <input
                    type="text"
                    v-model="form.reference_no"
                    class="form-control dq-input"
                    readonly
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Customer & Payment -->
          <div class="dq-card">
            <div class="dq-card-head">
              <div class="dq-card-title">
                <span class="dq-badge-icon dq-badge-purple"
                  ><i class="ti ti-user"></i
                ></span>
                <div>
                  <div class="dq-h">Customer & Payment</div>
                  <div class="dq-sub">
                    Customer selection + optional payment info.
                  </div>
                </div>
              </div>

              <a
                href="javascript:void(0);"
                class="dq-link"
                data-bs-toggle="modal"
                data-bs-target="#add_customer"
              >
                <i class="ti ti-plus me-2"></i>Add Customer
              </a>
            </div>

            <div class="dq-card-body">
              <div class="dq-grid-3">
                <div class="dq-field">
                  <label class="dq-label"
                    >Customer <span class="text-danger">*</span></label
                  >

                  <div class="dq-dd">
                    <button
                      type="button"
                      class="dq-dd-btn"
                      :class="
                        attempted && errors.client_id ? 'dq-dd-btn-err' : ''
                      "
                      @click="clientDdOpen ? closeClientDd() : openClientDd()"
                      @keydown="onClientKeydown"
                    >
                      <span class="dq-dd-text">{{
                        form.client_name || "Select customer"
                      }}</span>
                      <i class="ti ti-chevron-down"></i>
                    </button>

                    <div v-if="clientDdOpen" class="dq-dd-menu" @click.stop>
                      <div class="dq-dd-search">
                        <i class="ti ti-search"></i>
                        <input
                          ref="clientInputRef"
                          v-model="clientQuery"
                          @input="debClients(clientQuery)"
                          type="text"
                          class="dq-dd-input"
                          placeholder="Search clients… (↑ ↓ Enter Esc)"
                          autocomplete="off"
                        />
                      </div>

                      <div class="dq-dd-list">
                        <div v-if="clientsLoading" class="dq-dd-empty">
                          Loading clients…
                        </div>

                        <button
                          v-for="(c, idx) in clientOptions"
                          :key="c.id"
                          type="button"
                          class="dq-dd-item"
                          :class="[
                            idx === clientActiveIndex ? 'is-active' : '',
                            c.id === form.client_id ? 'is-selected' : '',
                          ]"
                          @mouseenter="clientActiveIndex = idx"
                          @click="chooseClient(c)"
                        >
                          <div class="dq-dd-item-main">
                            <div class="dq-dd-item-title">{{ c.label }}</div>
                            <div v-if="c.meta" class="dq-dd-item-sub">
                              {{ c.meta }}
                            </div>
                          </div>
                          <i
                            v-if="c.id === form.client_id"
                            class="ti ti-check"
                          ></i>
                        </button>

                        <div
                          v-if="!clientsLoading && clientOptions.length === 0"
                          class="dq-dd-empty"
                        >
                          No client found
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="attempted && errors.client_id" class="dq-err">
                    <i class="ti ti-alert-circle me-1"></i
                    >{{ errors.client_id }}
                  </div>
                  <div v-else class="dq-help">
                    Use ↑ ↓ and Enter for fast selection.
                  </div>
                </div>

                <div class="dq-field">
                  <label class="dq-label">Payment Type</label>
                  <vue-select
                    :options="Paymenttype"
                    v-model="form.payment_type"
                    id="credit-edit"
                    placeholder="Select"
                    class="dq-vselect"
                  />
                  <div class="dq-help">Optional.</div>
                </div>

                <div class="dq-field">
                  <label class="dq-label">Bank Name</label>
                  <input
                    type="text"
                    v-model="form.bank_name"
                    class="form-control dq-input"
                    placeholder="Optional"
                  />
                  <div class="dq-help">Only if applicable.</div>
                </div>
              </div>

              <div class="dq-grid-2 mt-3">
                <div class="dq-field">
                  <label class="dq-label">Linked Project</label>
                  <select
                    v-model="form.project_id"
                    class="form-select dq-input"
                    :disabled="!form.client_id"
                  >
                    <option :value="null">
                      {{ form.client_id ? "Select project" : "Choose a customer first" }}
                    </option>
                    <option
                      v-for="project in filteredProjectOptions"
                      :key="project.id"
                      :value="project.id"
                    >
                      {{ project.name }}
                    </option>
                  </select>
                  <div class="dq-help">Optional. Keeps finance tied to the delivery project.</div>
                </div>

                <div class="dq-field">
                  <label class="dq-label">Billing Contact Email</label>
                  <input
                    v-model="form.email"
                    type="email"
                    class="form-control dq-input"
                    placeholder="billing@client.com"
                  />
                  <div class="dq-help">Editable invoice snapshot, even if the CRM account changes later.</div>
                </div>
              </div>

              <div class="dq-grid-2 mt-3">
                <div class="dq-field">
                  <label class="dq-label">Client Address</label>
                  <textarea
                    v-model="form.client_address"
                    class="form-control dq-input"
                    rows="3"
                    placeholder="Client address"
                  ></textarea>
                </div>

                <div class="dq-field">
                  <label class="dq-label">Billing Address</label>
                  <textarea
                    v-model="form.billing_address"
                    class="form-control dq-input"
                    rows="3"
                    placeholder="Billing address"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          <!-- Items -->
          <div class="dq-card">
            <div class="dq-card-head">
              <div class="dq-card-title">
                <span class="dq-badge-icon dq-badge-green"
                  ><i class="ti ti-shopping-cart"></i
                ></span>
                <div>
                  <div class="dq-h">Items</div>
                  <div class="dq-sub">
                    Update line items, qty, rate and discount.
                  </div>
                </div>
              </div>

              <button
                type="button"
                class="btn btn-outline-primary dq-mini-btn"
                @click.prevent="addItem"
              >
                <i class="ti ti-plus me-1"></i>Add item
              </button>
            </div>

            <div class="dq-card-body">
              <div v-if="attempted && errors.items" class="dq-err mb-2">
                <i class="ti ti-alert-circle me-1"></i>{{ errors.items }}
              </div>

              <div class="dq-items">
                <div class="dq-items-head">
                  <div>Description</div>
                  <div class="text-end">Qty</div>
                  <div class="text-end">Rate</div>
                  <div class="text-end">Discount</div>
                  <div class="text-end">Total</div>
                  <div></div>
                </div>

                <div
                  v-for="(item, idx) in form.items"
                  :key="idx"
                  class="dq-items-row"
                >
                  <div class="dq-cell">
                    <input
                      :class="[
                        'form-control dq-input',
                        idx === form.items.length - 1
                          ? 'dq-item-desc-last'
                          : '',
                      ]"
                      type="text"
                      v-model="item.description"
                      placeholder="e.g. Payroll setup, Consulting, Subscription…"
                    />
                  </div>

                  <div class="dq-cell">
                    <input
                      class="form-control dq-input dq-right"
                      type="number"
                      min="1"
                      v-model.number="item.qty"
                      @input="computeRowTotal(item)"
                    />
                  </div>

                  <div class="dq-cell">
                    <input
                      class="form-control dq-input dq-right"
                      type="number"
                      min="0"
                      v-model.number="item.rate"
                      @input="computeRowTotal(item)"
                    />
                  </div>

                  <div class="dq-cell">
                    <input
                      class="form-control dq-input dq-right"
                      type="number"
                      min="0"
                      v-model.number="item.discount"
                      @input="computeRowTotal(item)"
                    />
                  </div>

                  <div class="dq-cell">
                    <input
                      class="form-control dq-input dq-right"
                      type="text"
                      :value="formatMoney(item.total)"
                      disabled
                    />
                  </div>

                  <div class="dq-cell dq-actions">
                    <button
                      v-if="form.items.length > 1"
                      type="button"
                      class="btn btn-light btn-icon dq-icon-btn"
                      @click="removeItem(idx)"
                      title="Remove"
                    >
                      <i class="ti ti-trash"></i>
                    </button>
                  </div>
                </div>
              </div>

              <button type="button" class="dq-addline" @click.prevent="addItem">
                <i class="ti ti-plus me-2"></i>Add another line
              </button>
            </div>
          </div>

          <!-- Tax -->
          <div class="dq-card">
            <div class="dq-card-head">
              <div class="dq-card-title">
                <span class="dq-badge-icon dq-badge-amber"
                  ><i class="ti ti-receipt-tax"></i
                ></span>
                <div>
                  <div class="dq-h">Tax</div>
                  <div class="dq-sub">
                    Optional. Uses your saved snapshot or selected active tax.
                  </div>
                </div>
              </div>

              <div class="form-check form-switch m-0">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="apply-tax-edit"
                  v-model="form.apply_tax"
                />
                <label class="form-check-label" for="apply-tax-edit"
                  >Apply</label
                >
              </div>
            </div>

            <div class="dq-card-body" v-if="form.apply_tax">
              <div class="dq-grid-2">
                <div class="dq-field">
                  <label class="dq-label">Select Tax</label>

                  <div class="dq-dd">
                    <button
                      type="button"
                      class="dq-dd-btn"
                      @click="taxDdOpen ? closeTaxDd() : openTaxDd()"
                      @keydown="onTaxKeydown"
                    >
                      <span class="dq-dd-text">{{ selectedTaxLabel }}</span>
                      <i class="ti ti-chevron-down"></i>
                    </button>

                    <div v-if="taxDdOpen" class="dq-dd-menu" @click.stop>
                      <div class="dq-dd-search">
                        <i class="ti ti-search"></i>
                        <input
                          ref="taxInputRef"
                          v-model="taxSearch"
                          type="text"
                          class="dq-dd-input"
                          placeholder="Search taxes… (↑ ↓ Enter Esc)"
                          autocomplete="off"
                          @input="taxActiveIndex = 0"
                        />
                      </div>

                      <div class="dq-dd-list">
                        <button
                          v-for="(t, idx) in filteredTaxOptions"
                          :key="t.id"
                          type="button"
                          class="dq-dd-item"
                          :class="[
                            idx === taxActiveIndex ? 'is-active' : '',
                            t.id === form.tax_id ? 'is-selected' : '',
                          ]"
                          @mouseenter="taxActiveIndex = idx"
                          @click="selectTax(t)"
                        >
                          <div class="dq-dd-item-main">
                            <div class="dq-dd-item-title">{{ t.label }}</div>
                            <div v-if="t.desc" class="dq-dd-item-sub">
                              {{ t.desc }}
                            </div>
                          </div>
                          <i
                            v-if="t.id === form.tax_id"
                            class="ti ti-check"
                          ></i>
                        </button>

                        <div
                          v-if="filteredTaxOptions.length === 0"
                          class="dq-dd-empty"
                        >
                          No tax found
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="dq-help" v-if="selectedTax">
                    Selected:
                    <span class="dq-strong">{{ selectedTax.name }}</span>
                  </div>
                </div>

                <div class="dq-field">
                  <label class="dq-label">Tax Amount</label>
                  <input
                    class="form-control dq-input dq-right"
                    type="text"
                    :value="formatMoney(taxTotal)"
                    disabled
                  />
                  <div class="dq-help">
                    Computed automatically from tax rate.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Notes -->
          <div class="dq-card">
            <div class="dq-card-head">
              <div class="dq-card-title">
                <span class="dq-badge-icon dq-badge-slate"
                  ><i class="ti ti-notes"></i
                ></span>
                <div>
                  <div class="dq-h">Additional Details</div>
                  <div class="dq-sub">
                    Optional details for customer / internal notes.
                  </div>
                </div>
              </div>
            </div>

            <div class="dq-card-body">
              <div class="dq-grid-2">
                <div class="dq-field">
                  <label class="dq-label">Description</label>
                  <textarea
                    class="form-control dq-textarea"
                    rows="3"
                    v-model="form.description"
                  ></textarea>
                </div>
                <div class="dq-field">
                  <label class="dq-label">Notes</label>
                  <textarea
                    class="form-control dq-textarea"
                    rows="3"
                    v-model="form.notes"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT: Summary -->
        <div class="dq-side">
          <div class="dq-sticky">
            <div class="dq-card dq-summary">
              <div class="dq-card-head">
                <div class="dq-card-title">
                  <span class="dq-badge-icon dq-badge-dark"
                    ><i class="ti ti-calculator"></i
                  ></span>
                  <div>
                    <div class="dq-h">Summary</div>
                    <div class="dq-sub">Live totals update as you edit.</div>
                  </div>
                </div>
              </div>

              <div class="dq-card-body">
                <div class="dq-kv">
                  <div class="dq-k">Subtotal</div>
                  <div class="dq-v">{{ formatMoney(subTotal) }}</div>
                </div>
                <div class="dq-kv">
                  <div class="dq-k">Discount</div>
                  <div class="dq-v">- {{ formatMoney(discountTotal) }}</div>
                </div>
                <div class="dq-kv" v-if="form.apply_tax">
                  <div class="dq-k">Tax ({{ taxRate }}%)</div>
                  <div class="dq-v">{{ formatMoney(taxTotal) }}</div>
                </div>

                <div class="dq-divider"></div>

                <div class="dq-kv dq-kv-total">
                  <div class="dq-k">Grand Total</div>
                  <div class="dq-v">{{ formatMoney(grandTotal) }}</div>
                </div>

                <div class="dq-actions-stack mt-3">
                  <button
                    type="button"
                    class="btn btn-dark w-100 dq-action-btn"
                    :disabled="saving"
                    v-can="'Invoice.Edit'"
                    @click="saveDraft"
                  >
                    <span
                      v-if="saving"
                      class="spinner-border spinner-border-sm me-2"
                    ></span>
                    <i v-else class="ti ti-device-floppy me-2"></i>
                    Save as Draft
                  </button>

                  <button
                    type="button"
                    class="btn btn-primary w-100 dq-action-btn"
                    :disabled="saving || !canSubmit"
                    v-can="'Invoice.Edit'"
                    @click="saveAndSend"
                  >
                    <span
                      v-if="saving"
                      class="spinner-border spinner-border-sm me-2"
                    ></span>
                    <i v-else class="ti ti-send me-2"></i>
                    Save &amp; Send
                  </button>

                  <div v-if="attempted && !canSubmit" class="dq-err mt-2">
                    <i class="ti ti-alert-circle me-1"></i>Please fix the form
                    errors to continue.
                  </div>

                  <a
                    href="javascript:void(0);"
                    class="dq-mini-link"
                    data-bs-toggle="modal"
                    data-bs-target="#invoice_preview"
                  >
                    <i class="ti ti-eye me-2"></i>Preview before sending
                  </a>
                </div>
              </div>
            </div>

            <div class="dq-card dq-hint mt-3">
              <div class="dq-card-body">
                <div class="dq-hint-row">
                  <i class="ti ti-bulb"></i>
                  <div>
                    <div class="dq-hint-title">Pro tip</div>
                    <div class="dq-hint-text">
                      Keep tax enabled only when you’re sure the customer
                      requires VAT / withholding.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div
      class="footer d-sm-flex align-items-center justify-content-between bg-white border-top p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="javascript:void(0);" class="text-primary"
          >DoneQuick Technological World</a
        >
      </p>
    </div>
  </div>

  <InvoicesModal
    :invoice="form"
    :items="form.items"
    :company="activeCompany"
    :customer="selectedCustomer"
    :totals="totalsForPreview"
  />
</template>

<style scoped>
/* ====== Page overlay loader ====== */
.dq-page-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.68);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.dq-overlay-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(16, 24, 40, 0.18);
  padding: 22px 18px;
  width: min(420px, calc(100% - 28px));
  text-align: center;
}
.dq-overlay-title {
  margin-top: 12px;
  font-weight: 950;
  color: rgba(17, 24, 39, 0.92);
}
.dq-overlay-sub {
  margin-top: 6px;
  font-size: 0.88rem;
  color: rgba(17, 24, 39, 0.6);
}

.dq-disabled {
  pointer-events: none;
  opacity: 0.65;
}

/* ====== Same design system as add-invoices.vue ====== */
.dq-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}
.dq-back {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: rgba(17, 24, 39, 0.9);
  font-weight: 800;
}
.dq-back-icon {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
}
.dq-top-actions .dq-preview {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  color: rgba(37, 99, 235, 1);
  text-decoration: none;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(37, 99, 235, 0.18);
  background: rgba(37, 99, 235, 0.06);
}

.dq-layout {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 16px;
}
@media (max-width: 992px) {
  .dq-layout {
    grid-template-columns: 1fr;
  }
}
.dq-sticky {
  position: sticky;
  top: 84px;
}

/* cards */
.dq-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  box-shadow: 0 16px 40px rgba(16, 24, 40, 0.06);
  overflow: hidden;
  margin-bottom: 14px;
}
.dq-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: linear-gradient(
    180deg,
    rgba(248, 250, 252, 1),
    rgba(255, 255, 255, 1)
  );
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.dq-card-body {
  padding: 14px 16px;
}

.dq-card-title {
  display: flex;
  align-items: center;
  gap: 10px;
}
.dq-h {
  font-weight: 950;
  letter-spacing: -0.02em;
  color: rgba(17, 24, 39, 0.92);
}
.dq-sub {
  font-size: 0.84rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 2px;
}

.dq-badge-icon {
  width: 38px;
  height: 38px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.12);
  color: rgba(37, 99, 235, 1);
}
.dq-badge-blue {
  background: rgba(59, 130, 246, 0.12);
  color: rgba(37, 99, 235, 1);
}
.dq-badge-purple {
  background: rgba(99, 102, 241, 0.12);
  color: rgba(79, 70, 229, 1);
}
.dq-badge-green {
  background: rgba(16, 185, 129, 0.12);
  color: rgba(5, 150, 105, 1);
}
.dq-badge-amber {
  background: rgba(245, 158, 11, 0.14);
  color: rgba(217, 119, 6, 1);
}
.dq-badge-slate {
  background: rgba(148, 163, 184, 0.18);
  color: rgba(51, 65, 85, 1);
}
.dq-badge-dark {
  background: rgba(17, 24, 39, 0.08);
  color: rgba(17, 24, 39, 0.9);
}

.dq-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 900;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(16, 185, 129, 0.08);
  color: rgba(5, 150, 105, 1);
}
.dq-link {
  display: inline-flex;
  align-items: center;
  font-weight: 900;
  text-decoration: none;
  color: rgba(37, 99, 235, 1);
}

/* inputs */
.dq-label {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.9);
  margin-bottom: 6px;
}
.dq-input {
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  min-height: 44px;
}
.dq-textarea {
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.dq-right {
  text-align: right;
}
.dq-help {
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 6px;
}
.dq-strong {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.9);
}
.dq-muted {
  color: rgba(17, 24, 39, 0.55);
}

.dq-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.dq-grid-3 {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 14px;
}
@media (max-width: 992px) {
  .dq-grid-2,
  .dq-grid-3 {
    grid-template-columns: 1fr;
  }
}

/* date */
.dq-date {
  position: relative;
}
.dq-date-ic {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(17, 24, 39, 0.45);
  z-index: 3;
  pointer-events: none;
}
.dq-date :deep(.datetimepicker) {
  padding-left: 40px !important;
}

/* v-select */
.dq-vselect :deep(.vs__dropdown-toggle) {
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.dq-vselect :deep(.vs__dropdown-menu) {
  border-radius: 14px;
  padding: 6px;
  box-shadow: 0 18px 40px rgba(16, 24, 40, 0.14);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

/* dropdown custom */
.dq-dd {
  position: relative;
}
.dq-dd-btn {
  width: 100%;
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  font-weight: 800;
  color: rgba(17, 24, 39, 0.85);
}
.dq-dd-btn-err {
  border-color: rgba(220, 38, 38, 0.35);
  box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.1);
}
.dq-dd-text {
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dq-dd-menu {
  position: absolute;
  z-index: 50;
  width: 100%;
  margin-top: 8px;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  box-shadow: 0 24px 60px rgba(16, 24, 40, 0.18);
  overflow: hidden;
}
.dq-dd-search {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(248, 250, 252, 1);
  color: rgba(17, 24, 39, 0.55);
}
.dq-dd-input {
  border: 0;
  outline: none;
  background: transparent;
  width: 100%;
  font-weight: 800;
  color: rgba(17, 24, 39, 0.85);
}
.dq-dd-list {
  max-height: 260px;
  overflow: auto;
  padding: 6px;
}
.dq-dd-item {
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  padding: 10px 10px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  transition: background 0.12s ease;
}
.dq-dd-item:hover,
.dq-dd-item.is-active {
  background: rgba(37, 99, 235, 0.08);
}
.dq-dd-item.is-selected {
  background: rgba(16, 185, 129, 0.1);
}
.dq-dd-item-main {
  min-width: 0;
}
.dq-dd-item-title {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dq-dd-item-sub {
  font-size: 0.78rem;
  color: rgba(17, 24, 39, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dq-dd-empty {
  padding: 14px 10px;
  text-align: center;
  color: rgba(17, 24, 39, 0.55);
  font-size: 0.85rem;
}

/* items */
.dq-items-head {
  display: grid;
  grid-template-columns: 2fr 0.8fr 1fr 1fr 1fr 44px;
  gap: 10px;
  font-size: 0.78rem;
  font-weight: 950;
  color: rgba(17, 24, 39, 0.55);
  padding: 6px 2px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  margin-bottom: 10px;
}
.dq-items-row {
  display: grid;
  grid-template-columns: 2fr 0.8fr 1fr 1fr 1fr 44px;
  gap: 10px;
  margin-bottom: 10px;
}
.dq-actions {
  display: flex;
  justify-content: flex-end;
}
.dq-icon-btn {
  border-radius: 12px;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
@media (max-width: 992px) {
  .dq-items-head {
    display: none;
  }
  .dq-items-row {
    grid-template-columns: 1fr;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 16px;
    padding: 12px;
    gap: 12px;
  }
  .dq-actions {
    justify-content: flex-start;
  }
}
.dq-addline {
  width: 100%;
  margin-top: 6px;
  border-radius: 14px;
  border: 1px dashed rgba(37, 99, 235, 0.35);
  background: rgba(37, 99, 235, 0.06);
  padding: 10px 12px;
  font-weight: 900;
  color: rgba(37, 99, 235, 1);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* summary */
.dq-kv {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
}
.dq-k {
  color: rgba(17, 24, 39, 0.6);
  font-weight: 800;
}
.dq-v {
  font-weight: 950;
  color: rgba(17, 24, 39, 0.92);
}
.dq-divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.06);
  margin: 10px 0;
}
.dq-kv-total .dq-k {
  color: rgba(17, 24, 39, 0.75);
}
.dq-kv-total .dq-v {
  font-size: 1.15rem;
}

.dq-action-btn {
  border-radius: 14px;
  padding: 12px 14px;
  font-weight: 900;
}
.dq-mini-link {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
  text-decoration: none;
  font-weight: 900;
  color: rgba(37, 99, 235, 1);
}
.dq-err {
  margin-top: 8px;
  font-size: 0.84rem;
  font-weight: 900;
  color: rgba(220, 38, 38, 1);
  display: flex;
  align-items: center;
}
.dq-hint-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  color: rgba(17, 24, 39, 0.7);
}
.dq-hint-title {
  font-weight: 950;
  color: rgba(17, 24, 39, 0.9);
  margin-bottom: 2px;
}
.dq-hint-text {
  font-size: 0.85rem;
  color: rgba(17, 24, 39, 0.6);
}

/* from */
/*
.dq-from-name {
  font-weight: 950;
  font-size: 1.05rem;
  color: rgba(17, 24, 39, 0.92);
}
.dq-from-line {
  color: rgba(17, 24, 39, 0.65);
  margin-top: 4px;
}
  */

/* From */
.dq-from-name {
  font-weight: 950;
  font-size: 1.05rem;
  color: rgba(17, 24, 39, 0.92);
  margin-left: 8px !important;
}
.dq-from-line {
  color: rgba(17, 24, 39, 0.65);
  margin-top: 4px;
  margin-left: 8px;
}
</style>


